import { isCancelledPatient, isScheduledElsewherePatient } from "./patientCareStatus";

export const FILTER_STATUS_MAP: Record<string, string[]> = {
  "Ready to Record": ["Ready to Record"],
  Review: ["Review"],
  "Processing Audio": ["Processing Audio"],
  Locked: ["Locked"],
  "Processing Audio + Review": ["Processing Audio", "Review"],
  "Processing Audio + Review + Locked": ["Processing Audio", "Review", "Locked"],
};

export const FILTER_PATIENT_TYPE_MAP: Record<string, string[]> = {
  "N/A": ["na"],
  Declined: ["declined"],
  Cancelled: ["cancelled"],
  "Scheduled Elsewhere": ["scheduled_elsewhere"],
};

const PATIENT_TYPE_FILTERS = new Set(Object.keys(FILTER_PATIENT_TYPE_MAP));

export type NoteListFilterPatient = {
  status?: string | null;
  patient_type?: string | null;
};

export const isEmptyPatientType = (patient: NoteListFilterPatient = {}): boolean => {
  const patientType = patient.patient_type;
  return patientType == null || patientType === "";
};

/**
 * Overlay care-status types that have their own filter checkboxes.
 * Visit types like "new" / "fu" stay on the status list.
 */
export const hasOverlayPatientType = (patient: NoteListFilterPatient = {}): boolean => {
  if (isCancelledPatient(patient) || isScheduledElsewherePatient(patient)) {
    return true;
  }

  const patientType = patient.patient_type;
  if (patientType == null || patientType === "") {
    return false;
  }

  return Object.values(FILTER_PATIENT_TYPE_MAP).some((types) => types.includes(patientType));
};

const selectedPatientTypeFilters = (filters: string[]): string[] =>
  filters.filter((filter) => PATIENT_TYPE_FILTERS.has(filter));

const selectedStatusFilters = (filters: string[]): string[] =>
  filters.filter((filter) => !PATIENT_TYPE_FILTERS.has(filter));

export const statusMatchesFilter = (
  patient: NoteListFilterPatient,
  filters: string[]
): boolean => {
  const statusFilters = selectedStatusFilters(filters);
  if (statusFilters.length === 0) {
    return false;
  }

  return statusFilters.some((filter) => {
    const allowedStatuses = FILTER_STATUS_MAP[filter];
    if (!allowedStatuses) {
      return patient?.status === filter;
    }
    return allowedStatuses.includes(patient?.status ?? "");
  });
};

export const patientTypeMatchesFilter = (
  patient: NoteListFilterPatient,
  filters: string[]
): boolean => {
  const typeFilters = selectedPatientTypeFilters(filters);
  if (typeFilters.length === 0) {
    return false;
  }

  return typeFilters.some((filter) => {
    if (filter === "Cancelled") {
      return isCancelledPatient(patient);
    }
    if (filter === "Scheduled Elsewhere") {
      return isScheduledElsewherePatient(patient);
    }
    const allowedPatientTypes = FILTER_PATIENT_TYPE_MAP[filter];
    return allowedPatientTypes?.includes(patient?.patient_type ?? "") ?? false;
  });
};

/**
 * Status filters only include patients with no overlay patient_type.
 * Overlay types (N/A, Declined, Cancelled, Scheduled Elsewhere) stay hidden
 * unless that patient_type filter is also selected.
 */
export const patientMatchesFilters = (
  patient: NoteListFilterPatient,
  filters: string[]
): boolean => {
  if (!filters || filters.length === 0) {
    return true;
  }

  const typeMatch = patientTypeMatchesFilter(patient, filters);
  if (hasOverlayPatientType(patient)) {
    return typeMatch;
  }

  return statusMatchesFilter(patient, filters);
};
